import { wizardConfig } from '../config/wizard-config';
import { authorizedFetch } from './session';

const API_BASE = wizardConfig.apiBaseUrl;

export type LoginLimitMode = 'off' | 'monitor' | 'block';
/** `hours` is the budget for an organization billed on logged-in time. */
export type LoginLimitType = 'concurrent' | 'named' | 'hours';
export type LoginLimitPeriodUnit = 'month' | 'day';
/** `other` includes the browser extension, which was a separate `chrome` choice before 2026-10-28. */
export type LoginLimitAdmitKind = 'workspace' | 'custom' | 'other';
export type LicenseTypeLimitAction = 'alert' | 'block';

/** Same rule as the gate and the toolbar. */
const LICENSE_TYPE_ID_RE = /^[A-Za-z0-9_.-]{1,64}$/;
export const MAX_LICENSE_TYPE_ROWS = 10;
/**
 * `CURATED_LICENSE_TYPE_IDS` in the backend, with the toolbar's `LOGIN_LIMIT_LICENSE_TYPES` names.
 * The admin route drops a saved row outside it on the next save. The names are never translated:
 * admins match them against their Genesys bill.
 */
const CURATED_LICENSE_TYPE_NAMES: Readonly<Record<string, string>> = Object.freeze({
  agentCopilotLicense: 'Genesys Agent Copilot',
  cxCloudSN: 'Unified Experience from Genesys and ServiceNow',
  communicate: 'Communicate',
  digitalAddOn: 'Digital add-on',
  cxCloudSF: 'CX Cloud from Genesys and Salesforce',
  salesforceClient: 'Genesys Cloud for Salesforce',
  workitemsLicense: 'Work Automation',
});
const CURATED_LICENSE_TYPE_IDS = new Set(Object.keys(CURATED_LICENSE_TYPE_NAMES));

export interface LicenseTypeLimit {
  id: string;
  limit: number;
  action: LicenseTypeLimitAction;
}

export interface LicenseTypeOption {
  id: string;
  name: string;
  counting: 'online' | 'unique' | null;
}

/** The license types the gate read with the License Manager client. */
export interface LicenseTypeOptions {
  state: 'ready' | 'not_connected' | 'unreadable';
  options: LicenseTypeOption[];
}

/** Customer fields from the organization row. The operator flag is not here. */
export interface LoginLimitSettings {
  loginLimitMode: LoginLimitMode;
  loginLimitType: LoginLimitType | null;
  loginLimitLimit: number | null;
  loginLimitPeriodStart: string | null;
  loginLimitPeriodUnit: LoginLimitPeriodUnit | null;
  loginLimitPeriodLength: number | null;
  loginLimitSlug: string | null;
  loginLimitAdmitKind: LoginLimitAdmitKind;
  loginLimitAdmitUrl: string | null;
  loginLimitCustomMessage: string;
  loginLimitAllowlist: string[];
  loginLimitAlertEmail: string;
  loginLimitAlertEnabled: boolean;
  loginLimitEverConnected: boolean;
  loginLimitAskForLoginId: boolean;
  loginLimitLoginIdLabel: string;
  /** Null when the organization has never stored this list. */
  loginLimitLicenseTypes: LicenseTypeLimit[] | null;
  /** Null when the organization has never stored the switch. Named treats that as on. */
  loginLimitExceptionsReport: boolean | null;
  /** Null when no report time zone is stored. */
  loginLimitReportTimeZone: string | null;
}

export interface LoginLimitStatus {
  connection: string;
  slug: string | null;
  loginUrl: string | null;
  genesysName: string | null;
  report: {
    state: string | null;
    model: 'concurrent' | 'named' | null;
    tier: number | null;
    digital: boolean;
    updatedAt: string | null;
    hourlyLoggedInWarning: boolean;
    /** The last complete report listed CX lines of both models, so `model` is null. False from an older gate. */
    mixed: boolean;
    /** The last billing report attempt failed; the gate retries at `retryAt`. */
    delayed: boolean;
    retryAt: string | null;
  } | null;
  /** True when connected and the gate's last Genesys read failed after its last good sample. False from an older gate. */
  genesysUnavailable: boolean;
  /** Time of that failure. The Genesys status code is never shown. */
  genesysLastFailureAt: string | null;
  hourlyInteractingExcluded: number | null;
  license: {
    readable: boolean;
    updatedAt: string | null;
    countingAllUsers: boolean;
    hourlyLicenseIdExposed: boolean;
  } | null;
  definitionTier: number | null;
  sample: { count: number | null; at: string | null; stale: boolean } | null;
  named: { counted: number | null; periodStart: string | null; periodEnd: string | null; label: string | null } | null;
  privateLink: boolean;
  nudge: string | null;
  copy: { connection: string; genesys: string; hourlyInteracting: string; license: string; updated: string } | null;
}

export interface LoginLimitView {
  enabled: boolean;
  available: boolean;
  settings: LoginLimitSettings | null;
  loginUrl: string | null;
  threatNotifyEmail: string;
  licenseTypeOptions: LicenseTypeOptions;
  /** Plaintext private link key. Empty means off, or the key was not stored. */
  privateKey: string;
  status: LoginLimitStatus | null;
  /** Only with `reportRetry`: whether the gate accepted the new billing report check. */
  reportCheckStarted?: boolean;
}

export interface LoginLimitSaveBody {
  loginLimitMode: LoginLimitMode;
  loginLimitType: LoginLimitType | null;
  loginLimitLimit: number | null;
  loginLimitPeriodStart: string | null;
  loginLimitPeriodUnit: LoginLimitPeriodUnit | null;
  loginLimitPeriodLength: number | null;
  loginLimitAdmitKind: LoginLimitAdmitKind;
  loginLimitAdmitUrl: string | null;
  loginLimitCustomMessage: string;
  loginLimitAllowlist: string[];
  loginLimitAlertEmail: string;
  loginLimitAlertEnabled: boolean;
  loginLimitAskForLoginId: boolean;
  loginLimitLoginIdLabel: string;
  loginLimitLicenseTypes?: LicenseTypeLimit[];
  loginLimitExceptionsReport?: boolean | null;
  loginLimitReportTimeZone?: string | null;
}

/**
 * Same rule as the server: only a save that starts enforcing needs a connection.
 * While the gate reports Genesys as not connected, Settings locks every field and the
 * server accepts only turning the limit Off.
 */
export function saveNeedsConnection(savedMode: LoginLimitMode, mode: LoginLimitMode): boolean {
  return (mode === 'block' && savedMode !== 'block') || (mode === 'monitor' && savedMode === 'off');
}

export class LoginLimitApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly field?: string,
    readonly permissions?: string[],
    readonly roleHold = false,
  ) {
    super(message);
    this.name = 'LoginLimitApiError';
  }
}

/** Throws SessionExpiredError (dialog shown) or GenesysUnavailableError instead of returning those. */
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  return authorizedFetch(url, options);
}

async function readBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return await response.json() as Record<string, unknown>;
  } catch {
    throw new LoginLimitApiError(`Unexpected server response (HTTP ${response.status})`);
  }
}

function apiError(body: Record<string, unknown>, fallback: string): LoginLimitApiError {
  const detail = body.detail && typeof body.detail === 'object' ? body.detail as Record<string, unknown> : {};
  const permissions = Array.isArray(detail.permissions)
    ? detail.permissions.filter((item): item is string => typeof item === 'string')
    : undefined;
  return new LoginLimitApiError(
    (typeof body.message === 'string' && body.message) || fallback,
    typeof body.error === 'string' ? body.error : undefined,
    typeof detail.field === 'string' ? detail.field : undefined,
    permissions,
    detail.roleHold === true,
  );
}

function isMode(value: unknown): value is LoginLimitMode {
  return value === 'off' || value === 'monitor' || value === 'block';
}

function isType(value: unknown): value is LoginLimitType {
  return value === 'concurrent' || value === 'named' || value === 'hours';
}

function isReportModel(value: unknown): value is 'concurrent' | 'named' {
  return value === 'concurrent' || value === 'named';
}

function isUnit(value: unknown): value is LoginLimitPeriodUnit {
  return value === 'month' || value === 'day';
}

export function isLicenseTypeId(value: unknown): value is string {
  return typeof value === 'string' && LICENSE_TYPE_ID_RE.test(value);
}

/** A saved id that can no longer have its own limit. */
export function isRetiredLicenseTypeId(id: string): boolean {
  return !CURATED_LICENSE_TYPE_IDS.has(id);
}

/** The Genesys name of a curated type, or null for any other id. */
export function curatedLicenseTypeName(id: string): string | null {
  return CURATED_LICENSE_TYPE_IDS.has(id) ? CURATED_LICENSE_TYPE_NAMES[id] : null;
}

function licenseTypeOptionsFrom(value: unknown): LicenseTypeOptions {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { state: 'unreadable', options: [] };
  const row = value as Record<string, unknown>;
  if (row.state === 'not_connected') return { state: 'not_connected', options: [] };
  if (row.state !== 'ready' || !Array.isArray(row.options)) return { state: 'unreadable', options: [] };
  const options: LicenseTypeOption[] = [];
  const seen = new Set<string>();
  for (const item of row.options) {
    if (!item || typeof item !== 'object') continue;
    const option = item as Record<string, unknown>;
    if (!isLicenseTypeId(option.id) || isRetiredLicenseTypeId(option.id) || seen.has(option.id)) continue;
    seen.add(option.id);
    const name = typeof option.name === 'string' && option.name.trim() ? option.name.trim() : option.id;
    const counting = option.counting === 'online' || option.counting === 'unique' ? option.counting : null;
    options.push({ id: option.id, name, counting });
  }
  return { state: 'ready', options };
}

function licenseTypeLimitsFrom(value: unknown): LicenseTypeLimit[] | null {
  if (value === undefined || value === null) return null;
  if (!Array.isArray(value)) return [];
  const rows: LicenseTypeLimit[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    if (!isLicenseTypeId(row.id) || seen.has(row.id)) continue;
    if (row.action !== 'alert' && row.action !== 'block') continue;
    if (typeof row.limit !== 'number' || !Number.isInteger(row.limit) || row.limit < 1) continue;
    seen.add(row.id);
    rows.push({ id: row.id, limit: row.limit, action: row.action });
  }
  return rows;
}

/** A stored `chrome` (the old browser extension choice) is shown and saved as `other`. */
function admitKindOf(value: unknown): LoginLimitAdmitKind | null {
  if (value === 'chrome') return 'other';
  return value === 'workspace' || value === 'custom' || value === 'other' ? value : null;
}

function settingsFrom(value: unknown): LoginLimitSettings | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const admitKind = admitKindOf(row.loginLimitAdmitKind);
  if (!isMode(row.loginLimitMode) || !admitKind) return null;
  return {
    loginLimitMode: row.loginLimitMode,
    loginLimitType: isType(row.loginLimitType) ? row.loginLimitType : null,
    loginLimitLimit: typeof row.loginLimitLimit === 'number' ? row.loginLimitLimit : null,
    loginLimitPeriodStart: typeof row.loginLimitPeriodStart === 'string' ? row.loginLimitPeriodStart : null,
    loginLimitPeriodUnit: isUnit(row.loginLimitPeriodUnit) ? row.loginLimitPeriodUnit : null,
    loginLimitPeriodLength: typeof row.loginLimitPeriodLength === 'number' ? row.loginLimitPeriodLength : null,
    loginLimitSlug: typeof row.loginLimitSlug === 'string' ? row.loginLimitSlug : null,
    loginLimitAdmitKind: admitKind,
    loginLimitAdmitUrl: typeof row.loginLimitAdmitUrl === 'string' ? row.loginLimitAdmitUrl : null,
    loginLimitCustomMessage: typeof row.loginLimitCustomMessage === 'string' ? row.loginLimitCustomMessage : '',
    loginLimitAllowlist: Array.isArray(row.loginLimitAllowlist)
      ? row.loginLimitAllowlist.filter((item): item is string => typeof item === 'string')
      : [],
    loginLimitAlertEmail: typeof row.loginLimitAlertEmail === 'string' ? row.loginLimitAlertEmail : '',
    loginLimitAlertEnabled: row.loginLimitAlertEnabled === true,
    loginLimitEverConnected: row.loginLimitEverConnected === true,
    loginLimitAskForLoginId: row.loginLimitAskForLoginId === true,
    loginLimitLoginIdLabel: typeof row.loginLimitLoginIdLabel === 'string' ? row.loginLimitLoginIdLabel : '',
    loginLimitLicenseTypes: licenseTypeLimitsFrom(
      Object.prototype.hasOwnProperty.call(row, 'loginLimitLicenseTypes') ? row.loginLimitLicenseTypes : undefined,
    ),
    loginLimitExceptionsReport: row.loginLimitExceptionsReport === true
      ? true
      : row.loginLimitExceptionsReport === false
        ? false
        : null,
    loginLimitReportTimeZone: typeof row.loginLimitReportTimeZone === 'string' ? row.loginLimitReportTimeZone : null,
  };
}

function statusFrom(value: unknown): LoginLimitStatus | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row.connection !== 'string') return null;
  const report = row.report && typeof row.report === 'object' ? row.report as Record<string, unknown> : null;
  const license = row.license && typeof row.license === 'object' ? row.license as Record<string, unknown> : null;
  const sample = row.sample && typeof row.sample === 'object' ? row.sample as Record<string, unknown> : null;
  const named = row.named && typeof row.named === 'object' ? row.named as Record<string, unknown> : null;
  const genesys = row.genesys && typeof row.genesys === 'object' ? row.genesys as Record<string, unknown> : null;
  const genesysUnavailable = row.connection === 'connected' && genesys?.state === 'unavailable';
  return {
    connection: row.connection,
    slug: typeof row.slug === 'string' ? row.slug : null,
    loginUrl: typeof row.loginUrl === 'string' ? row.loginUrl : null,
    genesysName: typeof row.genesysName === 'string' ? row.genesysName : null,
    report: report ? {
      state: typeof report.state === 'string' ? report.state : null,
      model: isReportModel(report.model) ? report.model : null,
      tier: typeof report.tier === 'number' ? report.tier : null,
      digital: report.digital === true,
      updatedAt: typeof report.updatedAt === 'string' ? report.updatedAt : null,
      hourlyLoggedInWarning: report.hourlyLoggedInWarning === true,
      mixed: report.mixed === true && !isReportModel(report.model),
      delayed: report.delayed === true,
      retryAt: typeof report.retryAt === 'string' && report.retryAt ? report.retryAt : null,
    } : null,
    genesysUnavailable,
    genesysLastFailureAt: genesysUnavailable && typeof genesys?.lastFailureAt === 'string' && genesys.lastFailureAt
      ? genesys.lastFailureAt
      : null,
    hourlyInteractingExcluded: typeof row.hourlyInteractingExcluded === 'number' ? row.hourlyInteractingExcluded : null,
    license: license ? {
      readable: license.readable === true,
      updatedAt: typeof license.updatedAt === 'string' ? license.updatedAt : null,
      countingAllUsers: license.countingAllUsers === true,
      hourlyLicenseIdExposed: license.hourlyLicenseIdExposed === true,
    } : null,
    definitionTier: typeof row.definitionTier === 'number' ? row.definitionTier : null,
    sample: sample ? {
      count: typeof sample.count === 'number' ? sample.count : null,
      at: typeof sample.at === 'string' ? sample.at : null,
      stale: sample.stale === true,
    } : null,
    named: named ? {
      counted: typeof named.counted === 'number' ? named.counted : null,
      periodStart: typeof named.periodStart === 'string' ? named.periodStart : null,
      periodEnd: typeof named.periodEnd === 'string' ? named.periodEnd : null,
      label: typeof named.label === 'string' ? named.label : null,
    } : null,
    privateLink: row.privateLink === true,
    nudge: typeof row.nudge === 'string' && row.nudge ? row.nudge : null,
    copy: null,
  };
}

function query(
  genesysOrgId: string,
  genesysRegion: string,
  sampleCheck: boolean,
  licenseTypesRetry = false,
  reportRetry = false,
): string {
  const params = new URLSearchParams({ genesysOrgId, genesysRegion });
  if (sampleCheck) params.set('sampleCheck', '1');
  if (licenseTypesRetry) params.set('licenseTypesRetry', '1');
  if (reportRetry) params.set('reportRetry', '1');
  return params.toString();
}

/**
 * `licenseTypesRetry` asks the gate to read the license types again when they were unreadable.
 * `reportRetry` asks the gate to read the billing report again (Check again). The returned status
 * is from before that check started.
 */
export async function getLoginLimit(
  genesysOrgId: string,
  genesysRegion: string,
  sampleCheck: boolean,
  licenseTypesRetry = false,
  reportRetry = false,
): Promise<LoginLimitView> {
  const response = await authFetch(
    `${API_BASE}/api/login-limit?${query(genesysOrgId, genesysRegion, sampleCheck, licenseTypesRetry, reportRetry)}`,
  );
  const body = await readBody(response);
  if (!response.ok) throw apiError(body, 'Unable to load License Manager.');
  if (body.enabled !== true) {
    return {
      enabled: false,
      available: false,
      settings: null,
      loginUrl: null,
      threatNotifyEmail: '',
      licenseTypeOptions: { state: 'not_connected', options: [] },
      privateKey: '',
      status: null,
    };
  }
  return {
    enabled: true,
    available: body.available === true,
    settings: settingsFrom(body.settings),
    loginUrl: typeof body.loginUrl === 'string' ? body.loginUrl : null,
    threatNotifyEmail: typeof body.threatNotifyEmail === 'string' ? body.threatNotifyEmail : '',
    licenseTypeOptions: licenseTypeOptionsFrom(body.licenseTypeOptions),
    privateKey: isPrivateKey(body.privateKey) ? body.privateKey : '',
    status: statusFrom(body.status),
    ...(typeof body.reportCheckStarted === 'boolean' ? { reportCheckStarted: body.reportCheckStarted } : {}),
  };
}

const PRIVATE_KEY_RE = /^[A-Z2-7]{12}$/;

function isPrivateKey(value: unknown): value is string {
  return typeof value === 'string' && PRIVATE_KEY_RE.test(value);
}

export interface PrivateLinkChange {
  enabled: boolean;
  key: string;
  url: string;
  saved: boolean;
}

/** Issues or clears the private link. A new key is returned once. */
export async function setLoginLimitPrivateLink(
  genesysOrgId: string,
  genesysRegion: string,
  enabled: boolean,
): Promise<PrivateLinkChange> {
  const response = await authFetch(`${API_BASE}/api/login-limit/private-link?${query(genesysOrgId, genesysRegion, false)}`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
  const body = await readBody(response);
  const key = isPrivateKey(body.key) ? body.key : '';
  const url = typeof body.url === 'string' ? body.url : '';
  if (!response.ok) {
    if (enabled && key) return { enabled: true, key, url, saved: false };
    throw apiError(body, 'The private link could not be changed.');
  }
  if (enabled && !key) throw new LoginLimitApiError('The private link could not be changed.');
  if (body.enabled !== enabled) throw new LoginLimitApiError('The private link could not be changed.');
  return { enabled, key, url, saved: true };
}

/** `{ loginLimitMode: 'off' }` alone is the only save the server accepts while Genesys is not connected. */
export async function saveLoginLimit(
  genesysOrgId: string,
  genesysRegion: string,
  settings: LoginLimitSaveBody | { loginLimitMode: 'off' },
): Promise<LoginLimitSettings> {
  const response = await authFetch(`${API_BASE}/api/login-limit?${query(genesysOrgId, genesysRegion, false)}`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  const body = await readBody(response);
  if (!response.ok) throw apiError(body, 'Unable to save License Manager settings.');
  const saved = settingsFrom(body.settings);
  if (!saved) throw new LoginLimitApiError('Unable to save License Manager settings.');
  return saved;
}

export async function connectLoginLimit(genesysOrgId: string, genesysRegion: string): Promise<void> {
  const response = await authFetch(`${API_BASE}/api/login-limit/connect?${query(genesysOrgId, genesysRegion, false)}`, {
    method: 'POST',
    body: '{}',
  });
  const body = await readBody(response);
  if (!response.ok) throw apiError(body, 'Unable to connect.');
}

export type LoginLimitVerifyCode =
  | 'ok'
  | 'invalid_client'
  | 'missing_role'
  | 'missing_permission'
  | 'genesys_unreachable';

export async function verifyLoginLimit(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<{ ok: boolean; code: LoginLimitVerifyCode }> {
  const response = await authFetch(`${API_BASE}/api/login-limit/verify?${query(genesysOrgId, genesysRegion, false)}`, {
    method: 'POST',
    body: '{}',
  });
  const body = await readBody(response);
  if (!response.ok) throw apiError(body, 'Unable to verify the connection.');
  const code = body.code;
  if (
    typeof body.ok !== 'boolean'
    || (code !== 'ok' && code !== 'invalid_client' && code !== 'missing_role'
      && code !== 'missing_permission' && code !== 'genesys_unreachable')
    || body.ok !== (code === 'ok')
  ) {
    throw new LoginLimitApiError('Unable to verify the connection.');
  }
  return { ok: body.ok, code };
}

/** `genesysCleanupComplete` is false only when the backend says the OAuth client was left in Genesys Cloud. */
export async function disconnectLoginLimit(
  genesysOrgId: string,
  genesysRegion: string,
): Promise<{ genesysCleanupComplete: boolean }> {
  const response = await authFetch(`${API_BASE}/api/login-limit/disconnect?${query(genesysOrgId, genesysRegion, false)}`, {
    method: 'POST',
    body: '{}',
  });
  const body = await readBody(response);
  if (!response.ok) throw apiError(body, 'Unable to disconnect.');
  return { genesysCleanupComplete: body.genesysCleanupComplete !== false };
}
